import React, { useMemo, useState } from 'react';
import ThumbnailStrip from '../Shared/ThumbnailStrip';
import { FaCalendarAlt, FaUser, FaWrench, FaClipboardList, FaImage, FaSort, FaSortUp, FaSortDown, FaCog } from 'react-icons/fa';

// Helper function to extract date string from date object or string
function getDateString(dateValue) {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') return dateValue;
  if (typeof dateValue === 'object' && dateValue.iso) return dateValue.iso;
  return '';
}

// Helper function to format date for display
function formatDate(dateValue) {
  const dateStr = getDateString(dateValue);
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return dateStr;
  }
}

export default function ServiceHistoryTable({ rows, onOpenInspectionDetails, showTitle = true, navigate, highlightEquipmentId }) {
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const normalized = Array.isArray(rows) ? rows : [];
  const normalizedHighlightId = highlightEquipmentId ? String(highlightEquipmentId).replace(/-/g, '') : null;

  // Helper function to get equipment name(s) as string for sorting/display
  const getEquipmentDisplay = (row) => {
    // New API may return arrays; support multiple possible shapes
    let names = [];

    if (Array.isArray(row.equipmentName)) {
      names = row.equipmentName;
    } else if (Array.isArray(row.equipmentNames)) {
      names = row.equipmentNames;
    } else if (Array.isArray(row.equipment)) {
      // Could be array of strings or objects
      names = row.equipment
        .map((item) => {
          if (typeof item === 'string') return item;
          if (!item || typeof item !== 'object') return null;
          return item.name || item.equipmentName || null;
        })
        .filter(Boolean);
    } else if (row.equipmentName) {
      names = [row.equipmentName];
    }

    if (!names.length) return 'N/A';
    return names.join(', ');
  };

  const sorted = useMemo(() => {
    const data = [...normalized];
    data.sort((a, b) => {
      let va = a[sortColumn];
      let vb = b[sortColumn];
      if (sortColumn === 'date') {
        const dateStrA = getDateString(va);
        const dateStrB = getDateString(vb);
        va = dateStrA ? new Date(dateStrA).getTime() : 0;
        vb = dateStrB ? new Date(dateStrB).getTime() : 0;
      } else if (sortColumn === 'equipmentName') {
        // Handle both array and string formats
        va = getEquipmentDisplay(a).toLowerCase();
        vb = getEquipmentDisplay(b).toLowerCase();
      } else {
        va = (va || '').toString().toLowerCase();
        vb = (vb || '').toString().toLowerCase();
      }
      if (va < vb) return sortDirection === 'asc' ? -1 : 1;
      if (va > vb) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [normalized, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sorted.length / rowsPerPage) || 1;
  const start = (currentPage - 1) * rowsPerPage;
  const pageData = sorted.slice(start, start + rowsPerPage);

  function handleSort(column) {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  }

  function getSortIcon(column) {
    if (sortColumn !== column) {
      return <FaSort style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }} />;
    }
    return sortDirection === 'asc' 
      ? <FaSortUp style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }} />
      : <FaSortDown style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }} />;
  }

  /** Gives Work Done / findings room before wrapping */
  const workDoneColumnStyle = {
    minWidth: '13.5rem',
    verticalAlign: 'top',
  };

  /** Keeps formatted dates (e.g. "Dec 31, 2024") on one line */
  const dateColumnStyle = {
    minWidth: '9.5rem',
    width: '9.5rem',
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  };

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', marginTop: 16, padding: '1rem' }}>
      {showTitle && (
        <div style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ 
            width: '20px', 
            height: '20px', 
            backgroundColor: '#dbeafe', 
            borderRadius: '4px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '12px',
            color: '#3b82f6'
          }}>
            <FaCog />
          </div>
          Scheduled Maintenance History
        </div>
      )}
      <div className="manager-table-scroll" role="region" aria-label="Maintenance history, scroll horizontally for all columns" tabIndex={0} style={{ overflowX: 'auto' }}>
        <table className="table mechanical-history-table" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151', ...workDoneColumnStyle }} tabIndex={0} aria-sort={sortColumn === 'findings' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('findings'); } }} onClick={() => handleSort('findings')}>
                Work Done{getSortIcon('findings')}
              </th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'equipmentName' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('equipmentName'); } }} onClick={() => handleSort('equipmentName')}>
                Equipment(s) Serviced{getSortIcon('equipmentName')}
              </th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151', ...dateColumnStyle }} tabIndex={0} aria-sort={sortColumn === 'date' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('date'); } }} onClick={() => handleSort('date')}>
                Date{getSortIcon('date')}
              </th>
              <th style={{ fontWeight: '600', color: '#374151' }}>Attachments</th>
              <th style={{ fontWeight: '600', color: '#374151', whiteSpace: 'nowrap', minWidth: '6.5rem' }}>Report</th>
            </tr>
          </thead>
          <tbody>
            {normalized.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontStyle: 'italic' }}>
                  No recent inspections
                </td>
              </tr>
            ) : (
              pageData.map((r) => (
              <tr
                key={r.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  const tds = e.currentTarget.querySelectorAll('td');
                  tds.forEach((td) => { td.style.backgroundColor = '#f3f4f6'; });
                }}
                onMouseLeave={(e) => {
                  const tds = e.currentTarget.querySelectorAll('td');
                  tds.forEach((td) => { td.style.backgroundColor = ''; });
                }}
                onClick={() => {
                  if (typeof onOpenInspectionDetails === 'function') onOpenInspectionDetails(r, 'service');
                }}
              >
                <td style={{ color: '#374151', ...workDoneColumnStyle }}>{r.findings || 'N/A'}</td>
                <td style={{ color: '#374151' }}>
                  {(() => {
                    // Build parallel arrays of names and IDs from various possible shapes
                    let names = [];
                    let ids = [];

                    if (Array.isArray(r.equipmentName)) {
                      names = r.equipmentName;
                    } else if (Array.isArray(r.equipmentNames)) {
                      names = r.equipmentNames;
                    } else if (Array.isArray(r.equipment)) {
                      names = r.equipment
                        .map((item) => {
                          if (typeof item === 'string') return item;
                          if (!item || typeof item !== 'object') return null;
                          return item.name || item.equipmentName || null;
                        })
                        .filter(Boolean);
                    } else if (r.equipmentName) {
                      names = [r.equipmentName];
                    }

                    // Support both equipmentId (string/array) and equipmentIds (array) from API
                    if (Array.isArray(r.equipmentId)) {
                      ids = r.equipmentId;
                    } else if (r.equipmentId) {
                      ids = [r.equipmentId];
                    } else if (Array.isArray(r.equipmentIds)) {
                      ids = r.equipmentIds;
                    }

                    if (!names.length) return 'N/A';

                    // If we have neither navigation nor a highlighted equipment, just show plain text
                    if (!navigate && (!normalizedHighlightId || !ids.length)) {
                      return names.join(', ');
                    }

                    // Render each name, optionally clickable and/or highlighted
                    return names.map((name, index) => {
                      const equipmentId = ids[index];
                      const cleanId = equipmentId ? String(equipmentId).replace(/-/g, '') : null;
                      const isHighlighted = normalizedHighlightId && cleanId === normalizedHighlightId;
                      const isClickable = !!equipmentId && !!navigate;

                      const baseStyle = {
                        fontWeight: isHighlighted ? '700' : '500',
                        color: isHighlighted ? '#1D4ED8' : '#374151',
                      };

                      const content = isClickable ? (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/mechanical-room/equipment/${equipmentId}`);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 'inherit',
                            ...baseStyle,
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#3b82f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = baseStyle.color;
                          }}
                        >
                          {name}
                        </button>
                      ) : (
                        <span key={index} style={baseStyle}>{name}</span>
                      );

                      return (
                        <React.Fragment key={`equip-${index}`}>
                          {index > 0 && ', '}
                          {content}
                        </React.Fragment>
                      );
                    });
                  })()}
                </td>
                <td style={{ color: '#374151', ...dateColumnStyle }}>{formatDate(r.date)}</td>
                <td>
                  {r.attachments?.length ? (
                    <ThumbnailStrip images={r.attachments} label="service attachment" onOpen={(index) => onOpenInspectionDetails?.({ ...r, initialImageIndex: index }, 'service')} />
                  ) : null}
                </td>
                <td>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof onOpenInspectionDetails === 'function') onOpenInspectionDetails(r, 'service');
                    }}
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#f3f4f6';
                    }}
                  >
                    View Report
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer controls - only show when there are inspections */}
      {normalized.length > 0 && (
        <div className="manager-table-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <div>
            <label style={{ marginRight: 8, color: '#6b7280' }}>Rows per page</label>
            <select
              aria-label="Rows per page"
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="form-select"
              style={{ width: 100, display: 'inline-block' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
          <div style={{ color: '#6b7280' }}>
            <button className="btn btn-light btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</button>
            <span style={{ margin: '0 8px' }}>{currentPage} / {totalPages}</span>
            <button className="btn btn-light btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}


